import { useMemo, useState } from 'react';
import { SortDescriptor } from '@ds';
import { renderCSTableCell } from './renderCsTableCell';
import { getCSColumns } from './config';
import { useHubsQuery } from '@/queries/hub';
import { useClinicalSpecialistsForAssignment, useReliefSpecialistsForAssignment } from '@/queries/resource';
import { DateString, Uuid } from '@/api/types';
import { CaseResourceCombined, DonorType, OrganType } from '@/api/apis/case';
import { CSAssignmentCandidate } from '@/api/apis/resources';
import { filterTrialProgramSpecialists, searchCandidates } from './utils';
import { mapSelected, sortCandidates } from '../utils';
import { CandidatesTable } from '../components/CandidatesTable/CandidatesTable';

type ClinicalSpecialistTableProps = {
  caseId: Uuid;
  hubId: Uuid;
  donor: DonorType;
  organ: OrganType;
  orTime: DateString;
  goTime: DateString;
  applyGoTime: boolean;
  assignedResources?: CaseResourceCombined[];
  isLogistics: boolean;
  searchTerm: string;
  onReserve: (csCandidate: CSAssignmentCandidate) => void;
  selectedSpecialistsIds: string[];
  isSwap?: boolean;
  caseName?: string;
  className?: string;
  trialProgramId: Uuid | undefined;
};

export const ClinicalSpecialistTable = ({
  caseId,
  hubId,
  donor,
  organ,
  orTime,
  goTime,
  applyGoTime,
  assignedResources = [],
  isLogistics,
  onReserve,
  searchTerm = '',
  selectedSpecialistsIds = [],
  isSwap = false,
  caseName,
  className,
  trialProgramId,
}: ClinicalSpecialistTableProps) => {
  const [sort, setSort] = useState<SortDescriptor | undefined>();
  const { data: hubs, isLoading: isHubsLoading } = useHubsQuery();
  const { data: orSpecialists, isLoading: isOrSpecialistsLoading } = useClinicalSpecialistsForAssignment(
    { caseId, hubId, donor, organ, orTime, trialProgramId },
    !applyGoTime,
  );

  const { data: reliefSpecialists, isLoading: isReliefSpecialistsLoading } = useReliefSpecialistsForAssignment(
    { caseId, hubId, donor, organ, goTime, trialProgramId },
    applyGoTime,
  );

  const isSpecialistsLoading = applyGoTime ? isReliefSpecialistsLoading : isOrSpecialistsLoading;
  const specialists = applyGoTime ? reliefSpecialists : orSpecialists;

  const tableRows = useMemo(() => {
    const mappedSpecialists = mapSelected(
      selectedSpecialistsIds,
      searchCandidates(searchTerm, specialists),
      'employeeId',
    );
    const filteredByTrialProgram = filterTrialProgramSpecialists(mappedSpecialists, organ, trialProgramId);
    if (isSwap) {
      const filteredSpecialists = filteredByTrialProgram.filter(
        (specialist) => !assignedResources.find((s) => s.resourceId === specialist.employeeId),
      );
      return sortCandidates(filteredSpecialists, sort);
    }
    return sortCandidates(filteredByTrialProgram, sort);
  }, [sort, searchTerm, specialists, selectedSpecialistsIds, assignedResources, isSwap, organ, trialProgramId]);

  const renderCell = useMemo(
    () => renderCSTableCell(isLogistics, donor, onReserve, hubs?.items, isSwap, caseName, applyGoTime),
    [onReserve, isLogistics, donor, hubs?.items, isSwap, caseName, applyGoTime],
  );

  return (
    <CandidatesTable
      label="Clinical specialists for assign"
      columns={getCSColumns(isSwap)}
      renderCell={renderCell}
      data={tableRows}
      isLoading={false}
      isFetching={isHubsLoading || isSpecialistsLoading}
      sortBy={sort}
      onSortByChange={setSort}
      className={className}
    />
  );
};
